import { AllHTMLAttributes, forwardRef, ReactElement, ReactNode } from 'react';

import classNames from 'classnames';

import { Box } from '../Box/Box';
import { Text } from '../Text/Text';
import { Loader } from '../Loader/Loader';

import type { TextProps } from '../Text/Text';

import { buttonStyle, loaderContainer, hidden } from './Button.css';

import { atoms } from '../../css/atoms/atoms.css';

type NativeButtonProps = AllHTMLAttributes<HTMLButtonElement>;

type ButtonSize = 'small' | 'regular' | 'large';
type ButtonVariant = 'solid' | 'outline' | 'text' | 'brand' | 'brandOutline' | 'danger' | 'dangerOutline';

export interface ButtonProps extends Omit<NativeButtonProps, 'height' | 'width' | 'size'> {
  children?: ReactNode;
  /** Controls the design of the button:
   * - `'solid'`
   * - `'outline'`
   * - `'text'`
   *
   * Defaults to a 'solid'
   */
  variant?: ButtonVariant;
  /** Used this to position the icon before button text */
  iconLeading?: ReactElement;
  /** Used this to position the icon after button text */
  iconTrailing?: ReactElement;
  /**
   * If set to `true` the button content is replaced with a loading animation.
   *
   * The button is also disabled.
   */
  loading?: boolean;
  /**
   * Used this to control what size the button renders
   * Currently two sizes are supported:
   * - regular
   * - small
   */
  size?: ButtonSize;
  as?: 'button' | 'a';
  type?: 'button' | 'reset' | 'submit';
  width?: 'full';
}

export interface ButtonTextProps {
  children?: ReactNode;
  variant: ButtonVariant;
  iconLeading?: ReactElement;
  iconTrailing?: ReactElement;
  loading?: boolean;
  size?: ButtonSize;
}

const resolveTextTone: {
  [key in ButtonVariant]: TextProps['tone'];
} = {
  solid: 'neutralInverted',
  outline: 'neutral',
  danger: 'neutralInverted',
  dangerOutline: 'danger',
  brand: 'neutralInverted',
  brandOutline: 'brandAccent',
  text: 'neutral',
};

const ButtonText = ({ variant, size, iconLeading, iconTrailing, loading, children }: ButtonTextProps) => (
  <>
    {iconLeading || iconTrailing ? (
      <Text
        size={size === 'small' ? 'small' : 'medium'}
        weight="demibold"
        tone={resolveTextTone[variant]}
        className={loading ? hidden : ''}
      >
        {!!iconLeading && (
          <span
            className={atoms({
              paddingRight: children ? 'xsmall' : 'none',
            })}
          >
            {iconLeading}
          </span>
        )}
        {children}
        {!!iconTrailing && (
          <span
            className={atoms({
              paddingLeft: children ? 'xsmall' : 'none',
            })}
          >
            {iconTrailing}
          </span>
        )}
      </Text>
    ) : (
      <Text
        size={size === 'small' ? 'small' : 'medium'}
        weight="demibold"
        tone={resolveTextTone[variant]}
        className={loading ? hidden : ''}
      >
        {children}
      </Text>
    )}
  </>
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      size = 'regular',
      loading,
      as = 'button',
      children,
      disabled,
      iconLeading,
      iconTrailing,
      width,
      className,
      ...restProps
    },
    ref
  ) => {
    const classNameWithButtonStyle = classNames(
      buttonStyle({
        size,
        variant,
      }),
      className
    );

    return (
      <Box
        component={as}
        display="block"
        position="relative"
        textAlign="center"
        borderRadius="large"
        cursor={!loading && !disabled ? 'pointer' : undefined}
        disabled={disabled || loading}
        width={width}
        className={classNameWithButtonStyle}
        {...restProps}
        ref={ref}
      >
        <ButtonText
          size={size}
          variant={variant}
          loading={loading}
          iconLeading={iconLeading}
          iconTrailing={iconTrailing}
        >
          {children}
        </ButtonText>
        {loading && (
          <Text
            tone={resolveTextTone[variant]}
            className={classNames(loaderContainer, !loading ? hidden : '')}
          >
            <Loader size={size === 'regular' ? 'small' : 'xsmall'} />
          </Text>
        )}
      </Box>
    );
  }
);

Button.displayName = 'Button';
